import React, { useState, useMemo } from "react";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Icon,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon, EditIcon, CheckIcon } from "@chakra-ui/icons";
import { AggregationType, ColumnType } from "build-ts/enums";
import {
  DatePeriodEnum,
  datePeriodEnumToJSON,
  GrowthBenchmarkProto,
  GrowthBenchmarkType,
} from "build-ts/relative_date";
import { FilterProto, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { getKGGrowthBenchmarks } from "utils/analysisUtils";

interface BenchmarksProps {
  benchmarkEntities: BenchmarkEntityProto[];
  selectedBenchmarks: BenchmarkEntityProto[];
  setSelectedBenchmarks: React.Dispatch<React.SetStateAction<BenchmarkEntityProto[]>>;
  benchmarkColumnHashes: ColumnHashProto[];
  setBenchmarkColumnHashes: React.Dispatch<React.SetStateAction<ColumnHashProto[]>>;
  selectedColumns: ObservationColumnProto[];
  selectedMetrics: MetricProto[];
  filters: FilterProto[];
}

const Benchmarks: React.FC<BenchmarksProps> = ({
  benchmarkEntities,
  selectedBenchmarks,
  setSelectedBenchmarks,
  benchmarkColumnHashes,
  setBenchmarkColumnHashes,
  selectedColumns,
  selectedMetrics,
  filters,
}) => {
  // State for the current benchmark selection
  const [currentSelection, setCurrentSelection] = useState<BenchmarkEntityProto | null>(null);
  // State for selected metrics for the current benchmark
  const [selectedMetricsForBenchmark, setSelectedMetricsForBenchmark] = useState<MetricProto[]>([]);

  // Derive measure columns from selectedColumns
  const selectedMeasureColumns = useMemo(
    () =>
      (selectedColumns || []).filter(
        (column) => column.kg_column_header?.column_type === ColumnType.MEASURE
      ),
    [selectedColumns]
  );

  // State for Last N Period Aggregation
  const [numberOfPeriods, setNumberOfPeriods] = useState<number>(4);
  // State for Generalized Growth Benchmark
  const [shiftPeriod, setShiftPeriod] = useState<DatePeriodEnum>(DatePeriodEnum.MONTH);
  const [generalizedNumberOfPeriods, setGeneralizedNumberOfPeriods] = useState<number>(1);
  const [generalizedAggregationType, setGeneralizedAggregationType] = useState<AggregationType>(
    AggregationType.AVERAGE
  );
  // State for custom names by metric/measure ID
  const [customNamesByActualsId, setCustomNamesByActualsId] = useState<Record<string, string>>({});

  // State for editing individual benchmark column names
  const [editingKey, setEditingKey] = useState<string>("");
  const [editingColumnName, setEditingColumnName] = useState<string>("");

  // Check if current selection is a Last N Period Aggregation benchmark
  const isLastNPeriodAggregation = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type ===
        GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("Last N") &&
      benchmarkName.includes("Average")
    );
  }, [currentSelection]);

  // Check if current selection is a Generalized Growth Benchmark (old style)
  const isGeneralizedGrowthBenchmark = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type ===
        GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName === "Generalized Growth Benchmark"
    );
  }, [currentSelection]);

  // Check if current selection is a N Period Ago benchmark (without aggregation)
  const isNPeriodAgo = useMemo(() => {
    if (!currentSelection?.growth_benchmark) return false;
    const benchmarkName = currentSelection.header?.name || "";
    return (
      currentSelection.growth_benchmark.growth_type ===
        GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("N ") &&
      benchmarkName.includes(" Ago") &&
      !currentSelection.growth_benchmark.aggregation_type
    );
  }, [currentSelection]);

  // Check if there are any date filters
  const hasDateFilters = useMemo(() => {
    if (!filters) return false;
    return filters.some((filter) => filter.date_filter !== undefined && filter.date_filter !== null);
  }, [filters]);

  // Filter benchmarks to only show growth benchmarks when date filters are present
  const availableBenchmarks = useMemo(() => {
    // Get regular benchmarks (non-growth)
    const regularBenchmarks = benchmarkEntities.filter(
      (benchmark) =>
        !benchmark.growth_benchmark || benchmark.growth_benchmark.growth_type === undefined
    );

    if (!hasDateFilters) {
      // If no date filters, only show regular benchmarks
      return regularBenchmarks;
    }

    // If date filters are present, generate dynamic growth benchmarks
    const dynamicGrowthBenchmarks = getKGGrowthBenchmarks(filters);

    // Combine regular benchmarks with dynamically generated growth benchmarks
    return [...regularBenchmarks, ...dynamicGrowthBenchmarks];
  }, [benchmarkEntities, filters, hasDateFilters]);

  // Handle benchmark selection
  const handleBenchmarkSelect = (benchmark: BenchmarkEntityProto) => {
    // Check if benchmark is already selected
    const isAlreadySelected = selectedBenchmarks.some(
      (selected) => selected.header?.id === benchmark.header?.id
    );

    if (isAlreadySelected) {
      // Show the benchmark selection but don't add it again
      setCurrentSelection(benchmark);
    } else {
      // Set as current selection
      setCurrentSelection(benchmark);
    }
    // Reset selected metrics for this benchmark and select all by default
    setSelectedMetricsForBenchmark(selectedMetrics || []);

    // If it's a Last N Period Aggregation, set the default values
    const benchmarkName = benchmark.header?.name || "";
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("Last N") &&
      benchmarkName.includes("Average")
    ) {
      if (benchmark.growth_benchmark.number) {
        setNumberOfPeriods(benchmark.growth_benchmark.number);
      }
    }

    // If it's a Generalized Growth Benchmark (old style), set the default values
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName === "Generalized Growth Benchmark"
    ) {
      if (benchmark.growth_benchmark.number) {
        setGeneralizedNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      if (benchmark.growth_benchmark.aggregation_type) {
        setGeneralizedAggregationType(benchmark.growth_benchmark.aggregation_type);
      }
      if (benchmark.growth_benchmark.growth_period) {
        setShiftPeriod(benchmark.growth_benchmark.growth_period);
      }
    }

    // If it's a N Period Ago benchmark, set the default values
    if (
      benchmark.growth_benchmark?.growth_type === GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK &&
      benchmarkName.includes("N ") &&
      benchmarkName.includes(" Ago") &&
      !benchmark.growth_benchmark.aggregation_type
    ) {
      if (benchmark.growth_benchmark.number) {
        setGeneralizedNumberOfPeriods(benchmark.growth_benchmark.number);
      }
      if (benchmark.growth_benchmark.growth_period !== undefined) {
        setShiftPeriod(benchmark.growth_benchmark.growth_period);
      } else {
        if (benchmarkName.includes("Day Ago")) {
          setShiftPeriod(DatePeriodEnum.DAY);
        } else if (benchmarkName.includes("Week Ago")) {
          setShiftPeriod(DatePeriodEnum.WEEK);
        } else if (benchmarkName.includes("Month Ago")) {
          setShiftPeriod(DatePeriodEnum.MONTH);
        } else if (benchmarkName.includes("Quarter Ago")) {
          setShiftPeriod(DatePeriodEnum.QUARTER);
        } else if (benchmarkName.includes("Year Ago")) {
          setShiftPeriod(DatePeriodEnum.YEAR);
        }
      }
    }
  };

  // Handle metric toggle for benchmark
  const handleMetricToggleForBenchmark = (metric: MetricProto) => {
    setSelectedMetricsForBenchmark((prev) => {
      const exists = prev.some((m) => m.header?.id === metric.header?.id);
      if (exists) {
        return prev.filter((m) => m.header?.id !== metric.header?.id);
      } else {
        return [...prev, metric];
      }
    });
  };

  // Handle Select All metrics toggle
  const handleSelectAllMetrics = () => {
    const allMetrics = selectedMetrics || [];
    const isAllSelected =
      allMetrics.length > 0 &&
      allMetrics.every((metric) =>
        selectedMetricsForBenchmark.some((selected) => selected.header?.id === metric.header?.id)
      );

    if (isAllSelected) {
      setSelectedMetricsForBenchmark([]);
    } else {
      setSelectedMetricsForBenchmark(allMetrics);
    }
  };

  // Check if all metrics are selected
  const isAllMetricsSelected = useMemo(() => {
    const allMetrics = selectedMetrics || [];
    return (
      allMetrics.length > 0 &&
      allMetrics.every((metric) =>
        selectedMetricsForBenchmark.some((selected) => selected.header?.id === metric.header?.id)
      )
    );
  }, [selectedMetrics, selectedMetricsForBenchmark]);

  // Check if we can add a benchmark (need selection and metrics or measures)
  const canAddBenchmark = useMemo(() => {
    return (
      currentSelection !== null &&
      (selectedMetricsForBenchmark.length > 0 || selectedMeasureColumns.length > 0)
    );
  }, [currentSelection, selectedMetricsForBenchmark.length, selectedMeasureColumns.length]);

  // Add current selection to the list
  const handleAddSelection = () => {
    if (
      !currentSelection ||
      (selectedMetricsForBenchmark.length === 0 && selectedMeasureColumns.length === 0)
    )
      return;

    // Check if benchmark is already in the selection
    const benchmarkExists = selectedBenchmarks.some(
      (b) => b.header?.id === currentSelection.header?.id
    );

    // Create a copy of the benchmark to modify if needed
    let benchmarkToAdd = { ...currentSelection };

    // If it's a Last N Period Aggregation, update its configuration
    if (isLastNPeriodAggregation && benchmarkToAdd.growth_benchmark) {
      const growthPeriod = benchmarkToAdd.growth_benchmark.growth_period;
      let shiftPeriodName = "PERIOD";
      switch (growthPeriod) {
        case DatePeriodEnum.DAY:
          shiftPeriodName = "DAY";
          break;
        case DatePeriodEnum.WEEK:
          shiftPeriodName = "WEEK";
          break;
        case DatePeriodEnum.MONTH:
          shiftPeriodName = "MONTH";
          break;
        case DatePeriodEnum.QUARTER:
          shiftPeriodName = "QUARTER";
          break;
        case DatePeriodEnum.YEAR:
          shiftPeriodName = "YEAR";
          break;
      }
      const customName = `LAST ${numberOfPeriods} ${shiftPeriodName} AVERAGE`;
      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName,
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: numberOfPeriods,
          aggregation_type: AggregationType.AVERAGE,
          growth_period: growthPeriod,
        }),
      };
    }

    // If it's a Generalized Growth Benchmark (old style), update its configuration
    if (isGeneralizedGrowthBenchmark && benchmarkToAdd.growth_benchmark) {
      let aggregationTypeName = "Average";
      switch (generalizedAggregationType) {
        case AggregationType.SUM:
          aggregationTypeName = "Sum";
          break;
        case AggregationType.MIN:
          aggregationTypeName = "Min";
          break;
        case AggregationType.MAX:
          aggregationTypeName = "Max";
          break;
        case AggregationType.COUNT:
          aggregationTypeName = "Count";
          break;
        case AggregationType.UNIQUE_COUNT:
          aggregationTypeName = "Unique Count";
          break;
        default:
          aggregationTypeName = "Average";
      }

      const shiftPeriodName =
        shiftPeriod.toString().charAt(0) + shiftPeriod.toString().slice(1).toLowerCase();
      const customName = `${generalizedNumberOfPeriods} ${shiftPeriodName}(s) ago ${aggregationTypeName}`;

      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName,
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: generalizedNumberOfPeriods,
          aggregation_type: generalizedAggregationType,
          growth_period: shiftPeriod,
        }),
      };
    }

    // If it's a N Period Ago benchmark, update its configuration
    if (isNPeriodAgo && benchmarkToAdd.growth_benchmark) {
      const shiftPeriodName =
        datePeriodEnumToJSON(shiftPeriod).charAt(0) +
        datePeriodEnumToJSON(shiftPeriod).slice(1).toLowerCase();
      const customName = `${generalizedNumberOfPeriods} ${shiftPeriodName}${generalizedNumberOfPeriods > 1 ? "s" : ""} Ago`;

      benchmarkToAdd = {
        ...benchmarkToAdd,
        header: {
          ...benchmarkToAdd.header,
          name: customName,
        },
        growth_benchmark: GrowthBenchmarkProto.create({
          growth_type: GrowthBenchmarkType.GENERALIZED_GROWTH_BENCHMARK,
          number: generalizedNumberOfPeriods,
          growth_period: shiftPeriod,
        }),
      };
    }

    // Update selectedBenchmarks if not already included
    const updatedBenchmarks = benchmarkExists
      ? selectedBenchmarks.map((b) =>
          b.header?.id === benchmarkToAdd.header?.id ? benchmarkToAdd : b
        )
      : [...selectedBenchmarks, benchmarkToAdd];

    // Create ColumnHashProto entries for benchmark + metrics or benchmark + measures
    const newBenchmarkColumnHashes: ColumnHashProto[] = [];

    // Create column hashes for each selected metric with this benchmark
    selectedMetricsForBenchmark.forEach((metric) => {
      if (metric.header?.id && benchmarkToAdd?.header?.id) {
        const columnHash: ColumnHashProto = {
          actuals_id: metric.header.id,
          benchmark_id: benchmarkToAdd.header.id,
        };
        const customName = customNamesByActualsId[metric.header.id];
        if (customName) columnHash.column_name = customName;
        newBenchmarkColumnHashes.push(columnHash);
      }
    });

    // Also create column hashes for measure columns with this benchmark
    selectedMeasureColumns.forEach((column) => {
      if (column.column_id && benchmarkToAdd?.header?.id) {
        const columnHash: ColumnHashProto = {
          actuals_id: column.column_id,
          benchmark_id: benchmarkToAdd.header.id,
        };
        const customName = customNamesByActualsId[column.column_id];
        if (customName) columnHash.column_name = customName;
        newBenchmarkColumnHashes.push(columnHash);
      }
    });

    // Combine with existing benchmark column hashes, removing any duplicates
    const updatedBenchmarkColumnHashes = [...benchmarkColumnHashes];
    newBenchmarkColumnHashes?.forEach((newColumn) => {
      const exists = updatedBenchmarkColumnHashes.some(
        (col) =>
          col.actuals_id === newColumn.actuals_id && col.benchmark_id === newColumn.benchmark_id
      );

      if (!exists) {
        updatedBenchmarkColumnHashes.push(newColumn);
      }
    });

    // Update state
    setSelectedBenchmarks(updatedBenchmarks);
    setBenchmarkColumnHashes(updatedBenchmarkColumnHashes);

    // Reset current selection and custom names
    setCurrentSelection(null);
    setSelectedMetricsForBenchmark([]);
    setCustomNamesByActualsId({});
  };

  // Remove a benchmark and its calculations
  const handleRemoveBenchmarkColumn = (benchmarkId: string, actualsId?: string) => {
    const updated = benchmarkColumnHashes.filter(
      (h) => !(h.benchmark_id === benchmarkId && h.actuals_id === actualsId)
    );
    setBenchmarkColumnHashes(updated);

    // Check if this was the last column hash for this benchmark
    const hasRemainingHashesForBenchmark = updated.some((h) => h.benchmark_id === benchmarkId);

    // If no remaining hashes for this benchmark, remove it from selectedBenchmarks
    if (!hasRemainingHashesForBenchmark) {
      const updatedBenchmarks = selectedBenchmarks.filter((b) => b.header?.id !== benchmarkId);
      setSelectedBenchmarks(updatedBenchmarks);
    }
  };

  // Handle editing a benchmark name
  const handleEditName = (benchmarkId: string, actualsId: string, newName: string) => {
    const updatedBenchmarkColumnHashes = benchmarkColumnHashes.map((columnHash) => {
      if (columnHash.benchmark_id === benchmarkId && columnHash.actuals_id === actualsId) {
        return {
          ...columnHash,
          column_name: newName || undefined,
        };
      }
      return columnHash;
    });
    setBenchmarkColumnHashes(updatedBenchmarkColumnHashes);
  };

  // Helpers for per-hash edit
  const handleStartEdit = (
    benchmarkId: string,
    actualsId: string | undefined,
    currentName: string
  ) => {
    const key = `${benchmarkId}::${actualsId || ""}`;
    setEditingKey(key);
    setEditingColumnName(currentName || "");
  };

  const handleSaveEditForKey = () => {
    if (!editingKey) return;
    const [bId, aId] = editingKey.split("::");
    if (bId && aId !== undefined) {
      handleEditName(bId, aId, editingColumnName);
    }
    setEditingKey("");
    setEditingColumnName("");
  };

  const handleCancelEditForKey = () => {
    setEditingKey("");
    setEditingColumnName("");
  };

  // Helper function to get benchmark name
  const getBenchmarkName = (benchmark: BenchmarkEntityProto | null): string => {
    return benchmark?.header?.name || "Unnamed Benchmark";
  };

  return (
    <Stack spacing={4}>
      {/* Benchmark Selection Dropdown */}
      <FormControl>
        <Menu closeOnSelect={true}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={availableBenchmarks.length === 0}
            fontWeight="600"
          >
            {currentSelection
              ? getBenchmarkName(currentSelection)
              : availableBenchmarks.length === 0
              ? "No benchmarks available"
              : "Select a benchmark"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {availableBenchmarks.map((benchmark, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleBenchmarkSelect(benchmark)}
                  _hover={{ bg: "purple.50" }}
                  fontWeight="600"
                >
                  <Flex alignItems="center" width="100%">
                    {getBenchmarkName(benchmark)}
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Last N Period Aggregation Configuration */}
      {isLastNPeriodAggregation && (
        <FormControl>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Box>
              <Text fontSize="sm" mb={1} fontWeight="600">
                # of Periods (N)
              </Text>
              <NumberInput
                min={1}
                value={numberOfPeriods}
                onChange={(_, value) => setNumberOfPeriods(value)}
                size="sm"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Box>
          </Box>
        </FormControl>
      )}

      {/* Generalized Growth Benchmark Configuration */}
      {isGeneralizedGrowthBenchmark && (
        <FormControl>
          <FormLabel>Generalized Growth Benchmark Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Stack spacing={3}>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>
                    Number of Periods
                  </Text>
                  <NumberInput
                    min={1}
                    value={generalizedNumberOfPeriods}
                    onChange={(_, value) => setGeneralizedNumberOfPeriods(value)}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
                <Box>
                  <Text fontSize="xs" mb={1}>
                    Aggregation Type
                  </Text>
                  <Select
                    size="sm"
                    value={generalizedAggregationType}
                    onChange={(e) =>
                      setGeneralizedAggregationType(parseInt(e.target.value, 10) as AggregationType)
                    }
                  >
                    <option value={AggregationType.SUM}>Sum</option>
                    <option value={AggregationType.AVERAGE}>Average</option>
                    <option value={AggregationType.COUNT}>Count</option>
                    <option value={AggregationType.UNIQUE_COUNT}>Unique Count</option>
                    <option value={AggregationType.MIN}>Min</option>
                    <option value={AggregationType.MAX}>Max</option>
                  </Select>
                </Box>
              </HStack>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>
                    Shift Period
                  </Text>
                  <Select
                    size="sm"
                    value={shiftPeriod}
                    onChange={(e) => setShiftPeriod(parseInt(e.target.value, 10) as DatePeriodEnum)}
                  >
                    <option value={DatePeriodEnum.DAY}>Day</option>
                    <option value={DatePeriodEnum.WEEK}>Week</option>
                    <option value={DatePeriodEnum.MONTH}>Month</option>
                    <option value={DatePeriodEnum.QUARTER}>Quarter</option>
                    <option value={DatePeriodEnum.YEAR}>Year</option>
                  </Select>
                </Box>
              </HStack>
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* N Period Ago Configuration (without aggregation) */}
      {isNPeriodAgo && (
        <FormControl>
          <FormLabel>N Period Ago Configuration</FormLabel>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
            <Stack spacing={3}>
              <HStack spacing={4}>
                <Box>
                  <Text fontSize="xs" mb={1}>
                    Number of Periods
                  </Text>
                  <NumberInput
                    min={1}
                    value={generalizedNumberOfPeriods}
                    onChange={(_, value) => setGeneralizedNumberOfPeriods(value)}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
              </HStack>
              <Text fontSize="xs" color="gray.600" fontStyle="italic">
                This benchmark compares with data from exactly N{" "}
                {datePeriodEnumToJSON(shiftPeriod).toLowerCase()}s ago without any aggregation.
              </Text>
            </Stack>
          </Box>
        </FormControl>
      )}

      {/* Metric Selection */}
      <FormControl>
        <Text mb={1} fontWeight="semibold" color="purple.700" textAlign="left">
          Select Metrics
        </Text>
        <Menu closeOnSelect={false}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            w="100%"
            textAlign="left"
            variant="outline"
            colorScheme="purple"
            isDisabled={!selectedMetrics || selectedMetrics.length === 0}
            fontWeight="600"
          >
            {selectedMetricsForBenchmark.length > 0
              ? `${selectedMetricsForBenchmark.length} metric(s) selected`
              : !selectedMetrics || selectedMetrics.length === 0
              ? "No metrics available"
              : "Select metrics"}
          </MenuButton>
          <Portal>
            <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
              {/* Select All Option */}
              <MenuItem
                onClick={handleSelectAllMetrics}
                _hover={{ bg: "purple.50" }}
                fontWeight="700"
                closeOnSelect={false}
                borderBottom="1px solid"
                borderColor="gray.200"
                mb={1}
              >
                <Checkbox
                  isChecked={isAllMetricsSelected}
                  pointerEvents="none"
                  mr={2}
                  colorScheme="purple"
                  mb={0}
                />
                Select All
              </MenuItem>
              {selectedMetrics?.map((metric, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleMetricToggleForBenchmark(metric)}
                  _hover={{ bg: "purple.50" }}
                  fontWeight="600"
                  closeOnSelect={false}
                >
                  <Checkbox
                    isChecked={selectedMetricsForBenchmark.some(
                      (m) => m.header?.id === metric.header?.id
                    )}
                    pointerEvents="none"
                    mr={2}
                    colorScheme="purple"
                    mb={0}
                  />
                  {metric.header?.name || "Unnamed Metric"}
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      </FormControl>

      {/* Add Benchmark Button - visible when a benchmark and metrics/measures are selected */}
      {canAddBenchmark && (
        <FormControl>
          <Button
            colorScheme="purple"
            onClick={handleAddSelection}
            width="100%"
            bg="purple.700"
            fontWeight="700"
            _hover={{ bg: "purple.600" }}
          >
            Add Benchmark Analysis
          </Button>
        </FormControl>
      )}

      {/* Display selected benchmark analyses grouped by benchmark */}
      {benchmarkColumnHashes.length > 0 && (
        <Box mt={4}>
          <Text fontWeight="medium" mb={2} color="purple.700" textAlign="left">
            Selected Benchmark Analyses:
          </Text>
          <Stack spacing={2}>
            {(() => {
              // Group column hashes by benchmark_id
              const groupedByBenchmark = benchmarkColumnHashes.reduce(
                (acc, hash) => {
                  const benchmarkId = hash.benchmark_id || "";
                  if (!acc[benchmarkId]) {
                    acc[benchmarkId] = [];
                  }
                  acc[benchmarkId].push(hash);
                  return acc;
                },
                {} as Record<string, ColumnHashProto[]>
              );

              return Object.entries(groupedByBenchmark).flatMap(([benchmarkId, hashes]) => {
                const benchmark = selectedBenchmarks?.find((b) => b.header?.id === benchmarkId);
                const benchmarkName = benchmark?.header?.name || "Unknown Benchmark";

                return hashes.map((hash, idx) => {
                  const key = `${benchmarkId}::${hash.actuals_id || idx}`;

                  // Find metric name
                  const metric = selectedMetrics?.find((m) => m.header?.id === hash.actuals_id);
                  // Find measure name from selectedColumns
                  const measure = selectedColumns?.find(
                    (c) =>
                      c.column_id === hash.actuals_id &&
                      c.kg_column_header?.column_type === ColumnType.MEASURE
                  );

                  const actualsName =
                    metric?.header?.name || measure?.kg_column_header?.name || "Unknown";
                  const displayName = (hash as any).column_name || actualsName;

                  return (
                    <Box
                      key={key}
                      borderWidth="1px"
                      borderRadius="md"
                      p={3}
                      backgroundColor="gray.50"
                      borderColor="purple.700"
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Box flex="1" mr={2}>
                          {editingKey === key && (
                            <>
                              <Input
                                value={editingColumnName}
                                onChange={(e) => setEditingColumnName(e.target.value)}
                                placeholder="Enter column name"
                                size="sm"
                                bg="white"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveEditForKey();
                                  } else if (e.key === "Escape") {
                                    handleCancelEditForKey();
                                  }
                                }}
                              />
                              <HStack spacing={1}>
                                <IconButton
                                  aria-label="Save column name"
                                  icon={<CheckIcon />}
                                  size="xs"
                                  onClick={handleSaveEditForKey}
                                  variant="ghost"
                                  colorScheme="green"
                                />
                                <IconButton
                                  aria-label="Cancel edit"
                                  icon={<CloseIcon />}
                                  size="xs"
                                  onClick={handleCancelEditForKey}
                                  variant="ghost"
                                />
                              </HStack>
                            </>
                          )}
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            color="purple.800"
                            noOfLines={1}
                            title={displayName}
                            textAlign="left"
                          >
                            {displayName}
                          </Text>
                          <Box>
													  <FormLabel mb={0} fontSize="xs" color="gray.600">Actuals</FormLabel>
                            <Box mb={1} textAlign="left">
                              <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                                {actualsName}
                              </Box>
                            </Box>
                            <FormLabel mb={0} fontSize="xs" color="gray.600">Benchmark</FormLabel>
                            <Box mb={1} textAlign="left">
                              <Box as="span" bg="blue.100" color="blue.800" px={2} py={0.5} borderRadius="md" fontSize="sm">
                                {benchmarkName}
                                </Box>
                            </Box>
                          </Box>
                        </Box>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Edit column name"
                            icon={<Icon as={EditIcon} boxSize={4} color="purple.700" />}
                            size="xs"
                            onClick={() =>
                              handleStartEdit(
                                benchmarkId,
                                hash.actuals_id,
                                (hash as any).column_name || displayName
                              )
                            }
                            variant="ghost"
                            colorScheme="purple"
                            _hover={{ bg: "purple.100" }}
                          />
                          <IconButton
                            aria-label="Remove benchmark column"
                            icon={<Icon as={CloseIcon} boxSize={3} />}
                            size="xs"
                            onClick={() => handleRemoveBenchmarkColumn(benchmarkId, hash.actuals_id)}
                            variant="ghost"
                            colorScheme="red"
                          />
                        </HStack>
                      </Flex>
                    </Box>
                  );
                });
              });
            })()}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default Benchmarks;
